import { useMutation, useMutationState } from '@tanstack/react-query';
import { toast } from '@evaneos/b2b-ui';
import { IntlShape, useIntl } from 'react-intl';
import { produce } from 'immer';
import { differenceInDays } from 'date-fns';
import { useMemo } from 'react';

import { StepName } from '../../design-system/types';
import { queryClient } from '../../shared/providers/react-query-provider';
import { getDossierOverviewsQueryKey } from '../dossier-overview/hooks';

import { debounce } from '../pipeline/utils';

import {
    changeStepApi,
    changeTaskDueDate,
    changeMemo,
    invalidateDuplicate,
    postChangeTask,
    postChangeTaskNote,
    postpone,
    reassign,
    reopen,
    specifyDeferAgreement,
    updateBudget,
    updateDatesOfServices,
    updateHeatLevel,
    updatePaxNumber,
    updateRequesterInformation,
    addVoucher,
    deleteVoucher,
    uploadVouchers,
    validateDuplicate,
} from './api';
import {
    DossierId,
    SerializedDossiers,
    TaskDefinition,
    TaskNote,
    Memo,
    SerializedDossier,
} from './types';
import {
    applyTaskChangeToCache,
    deleteDossierInCache,
    refreshDossierAndPropagateToOverviews,
} from './cache-utils';
import { generateUploadVouchersFormData } from './operations';
import { getDossierQueryKey } from './queries';

const changeStepMutationKey = ['changeStep'];
const getChangeTaskMutationKey = (dossierId: DossierId) => ['changeTask', dossierId, 'definition'];
const getChangeTaskDueDateMutationKey = (dossierId: DossierId) => [
    'changeTask',
    dossierId,
    'dueDate',
];
const getChangeTaskNoteMutationKey = (dossierId: DossierId) => ['changeTask', dossierId, 'note'];

export type ChangeStepMutationVariables = {
    dossierId: DossierId;
    nextStep: StepName;
};

export const useChangeStepMutation = () => {
    const { formatMessage } = useIntl();

    return useMutation({
        mutationKey: changeStepMutationKey,
        mutationFn: ({ dossierId, nextStep }: ChangeStepMutationVariables) =>
            changeStepApi(dossierId, nextStep),
        onMutate: async ({ dossierId, nextStep }) => {
            const dossierOverviewsQueryKey = getDossierOverviewsQueryKey();
            await queryClient.cancelQueries({ queryKey: dossierOverviewsQueryKey });
            const previousOverviewData =
                queryClient.getQueryData<SerializedDossiers>(dossierOverviewsQueryKey);
            queryClient.setQueryData<SerializedDossiers>(dossierOverviewsQueryKey, (cur) => {
                if (!cur) return cur;
                return produce(cur, (draft) => {
                    if (draft.dossier[dossierId]) {
                        draft.dossier[dossierId].step = nextStep;
                    }
                });
            });

            const dossierQueryKey = getDossierQueryKey(dossierId);
            const previousDossierData =
                queryClient.getQueryData<SerializedDossier>(dossierQueryKey);
            await queryClient.cancelQueries({ queryKey: dossierQueryKey });
            queryClient.setQueryData<SerializedDossier>(dossierQueryKey, (cur) => {
                if (!cur) return cur;
                return produce(cur, (draft) => {
                    if (draft.dossier[dossierId]) {
                        draft.dossier[dossierId].step = nextStep;
                    }
                });
            });

            return { previousOverviewData, previousDossierData, dossierId };
        },
        onSuccess: async (_, { dossierId }) => {
            await refreshDossierAndPropagateToOverviews(dossierId);
            toast.success(formatMessage({ id: 'dossier.notifications.success' }));
        },
        onError: (_, __, context) => {
            if (context?.previousOverviewData) {
                queryClient.setQueryData(
                    getDossierOverviewsQueryKey(),
                    context.previousOverviewData,
                );
            }
            if (context?.previousDossierData) {
                queryClient.setQueryData(
                    getDossierQueryKey(context.dossierId),
                    context.previousDossierData,
                );
            }
            onErrorDefault(formatMessage);
        },
    });
};

export function useChangeStepStatus(dossierId: string) {
    const items = useMutationState({
        filters: {
            mutationKey: changeStepMutationKey,
        },
        select: (mutation) => {
            const vars = mutation.state.variables as ChangeStepMutationVariables | undefined;
            if (vars?.dossierId !== dossierId) return null;

            return {
                status: mutation.state.status,
                error: mutation.state.error as Error | null,
                nextStep: vars?.nextStep,
                submittedAt: mutation.state.submittedAt ?? 0,
            };
        },
    });

    const filtered = items.filter((item) => item !== null);
    const latest = filtered.sort((a, b) => b.submittedAt - a.submittedAt).at(0);

    return useMemo(
        () => ({
            isPending: latest?.status === 'pending',
            isError: latest?.status === 'error',
            error: latest?.error ?? null,
            stepInError: latest?.error ? latest?.nextStep : null,
        }),
        [latest?.status, latest?.error, latest?.nextStep],
    );
}

export const useUpdateDatesOfServicesMutation = (dossierId: DossierId) => {
    const { formatMessage } = useIntl();

    return useMutation({
        mutationFn: ({ startDate, endDate }: { startDate: Date; endDate: Date }) =>
            updateDatesOfServices(dossierId, startDate, endDate),
        onMutate: async ({ startDate, endDate }) => {
            const dossierQueryKey = getDossierQueryKey(dossierId);

            await queryClient.cancelQueries({ queryKey: dossierQueryKey });

            const previousDossierData =
                queryClient.getQueryData<SerializedDossier>(dossierQueryKey);

            const duration = differenceInDays(endDate, startDate) + 1;

            queryClient.setQueryData<SerializedDossier>(dossierQueryKey, (cur) => {
                if (!cur) return cur;
                return produce(cur, (draft) => {
                    if (draft.dossier[dossierId]?.project) {
                        draft.dossier[dossierId].project.expectedArrivalDate = startDate;
                        draft.dossier[dossierId].project.departureDate = endDate;
                        draft.dossier[dossierId].project.duration = duration;
                    }
                });
            });

            return { previousDossierData };
        },
        onSuccess: async () => {
            await refreshDossierAndPropagateToOverviews(dossierId);
            toast.success(formatMessage({ id: 'dossier.notifications.update_success' }));
        },
        onError: (_, __, context) => {
            if (context?.previousDossierData) {
                queryClient.setQueryData(
                    getDossierQueryKey(dossierId),
                    context.previousDossierData,
                );
            }
            onErrorDefault(formatMessage);
        },
    });
};

export const useUpdatePaxNumberMutation = (dossierId: DossierId) => {
    const { formatMessage } = useIntl();

    return useMutation({
        mutationFn: ({ paxNumber }: { paxNumber: number }) => updatePaxNumber(dossierId, paxNumber),
        onMutate: async ({ paxNumber }) => {
            const dossierQueryKey = getDossierQueryKey(dossierId);

            await queryClient.cancelQueries({ queryKey: dossierQueryKey });

            const previousDossierData =
                queryClient.getQueryData<SerializedDossier>(dossierQueryKey);

            queryClient.setQueryData<SerializedDossier>(dossierQueryKey, (cur) => {
                if (!cur) return cur;
                return produce(cur, (draft) => {
                    if (draft.dossier[dossierId]?.project) {
                        draft.dossier[dossierId].project.paxNumber = paxNumber;
                    }
                });
            });

            return { previousDossierData };
        },
        onSuccess: async () => {
            await refreshDossierAndPropagateToOverviews(dossierId);
            toast.success(formatMessage({ id: 'dossier.notifications.update_success' }));
        },
        onError: (_, __, context) => {
            if (context?.previousDossierData) {
                queryClient.setQueryData(
                    getDossierQueryKey(dossierId),
                    context.previousDossierData,
                );
            }
            onErrorDefault(formatMessage);
        },
    });
};

export const useUpdateRequesterInformationMutation = (dossierId: DossierId) => {
    const { formatMessage } = useIntl();

    return useMutation({
        mutationFn: ({ name, phoneNumber }: { name: string; phoneNumber: string }) =>
            updateRequesterInformation(dossierId, name, phoneNumber),
        onMutate: async ({ name, phoneNumber }) => {
            const dossierQueryKey = getDossierQueryKey(dossierId);

            await queryClient.cancelQueries({ queryKey: dossierQueryKey });

            const previousDossierData =
                queryClient.getQueryData<SerializedDossier>(dossierQueryKey);

            queryClient.setQueryData<SerializedDossier>(dossierQueryKey, (cur) => {
                if (!cur) return cur;
                return produce(cur, (draft) => {
                    if (draft.dossier[dossierId]?.project) {
                        draft.dossier[dossierId].project.requesterName = name;
                        draft.dossier[dossierId].project.requesterPhone = phoneNumber;
                    }
                });
            });

            return { previousDossierData };
        },
        onSuccess: async () => {
            await refreshDossierAndPropagateToOverviews(dossierId);
            toast.success(formatMessage({ id: 'dossier.notifications.update_success' }));
        },
        onError: (_, __, context) => {
            if (context?.previousDossierData) {
                queryClient.setQueryData(
                    getDossierQueryKey(dossierId),
                    context.previousDossierData,
                );
            }
            onErrorDefault(formatMessage);
        },
    });
};

export const useReassignAgentMutation = (dossierId: DossierId) => {
    const { formatMessage } = useIntl();

    return useMutation({
        mutationFn: ({ agentId }: { agentId: string }) => reassign(dossierId, agentId),
        onMutate: async ({ agentId }) => {
            const dossierQueryKey = getDossierQueryKey(dossierId);

            await queryClient.cancelQueries({ queryKey: dossierQueryKey });

            const previousDossierData =
                queryClient.getQueryData<SerializedDossier>(dossierQueryKey);

            queryClient.setQueryData<SerializedDossier>(dossierQueryKey, (cur) => {
                if (!cur) return cur;
                return produce(cur, (draft) => {
                    if (draft.dossier[dossierId]) {
                        draft.dossier[dossierId].agentId = agentId;
                    }
                });
            });

            return { previousDossierData };
        },
        onSuccess: async () => {
            await refreshDossierAndPropagateToOverviews(dossierId);
            toast.success(formatMessage({ id: 'dossier.notifications.reassign_success' }));
        },
        onError: (_, __, context) => {
            if (context?.previousDossierData) {
                queryClient.setQueryData(
                    getDossierQueryKey(dossierId),
                    context.previousDossierData,
                );
            }
            onErrorDefault(formatMessage);
        },
    });
};

export const useUpdateBudgetMutation = (dossierId: DossierId) => {
    const { formatMessage } = useIntl();

    return useMutation({
        mutationFn: ({ budget }: { budget: number | null }) => updateBudget(dossierId, budget),
        onSuccess: async () => {
            toast.success(formatMessage({ id: 'dossier.notifications.update_success' }));
            await refreshDossierAndPropagateToOverviews(dossierId);
        },
        onError: () => onErrorDefault(formatMessage),
    });
};

export const usePostponeMutation = (dossierId: DossierId) => {
    const { formatMessage } = useIntl();

    return useMutation({
        mutationFn: ({ dueDate }: { dueDate: string }) => postpone(dossierId, dueDate),
        onSuccess: async () => {
            await refreshDossierAndPropagateToOverviews(dossierId);
            toast.success(formatMessage({ id: 'dossier.notifications.success' }));
        },
        onError: () => onErrorDefault(formatMessage),
    });
};

export const useChangeTaskMutation = (dossierId: DossierId) => {
    const { formatMessage } = useIntl();

    return useMutation({
        mutationKey: getChangeTaskMutationKey(dossierId),
        mutationFn: ({
            taskDefinition,
            taskNote,
        }: {
            taskDefinition: TaskDefinition;
            taskNote?: TaskNote;
        }) => postChangeTask(dossierId, taskDefinition, taskNote),
        onMutate: async ({ taskDefinition, taskNote }) => {
            const dossierQueryKey = getDossierQueryKey(dossierId);

            await queryClient.cancelQueries({ queryKey: dossierQueryKey });

            const previousDossierData =
                queryClient.getQueryData<SerializedDossier>(dossierQueryKey);

            queryClient.setQueryData<SerializedDossier>(dossierQueryKey, (cur) => {
                if (!cur) return cur;
                return applyTaskChangeToCache(cur, dossierId, { taskDefinition, taskNote });
            });

            return { previousDossierData };
        },
        onSuccess: () => refreshDossierAndPropagateToOverviews(dossierId),
        onError: (_, __, context) => {
            if (context?.previousDossierData) {
                queryClient.setQueryData(
                    getDossierQueryKey(dossierId),
                    context.previousDossierData,
                );
            }
            onErrorDefault(formatMessage);
        },
    });
};

export const useChangeTaskDueDateMutation = (dossierId: DossierId) => {
    const { formatMessage } = useIntl();

    return useMutation({
        mutationKey: getChangeTaskDueDateMutationKey(dossierId),
        mutationFn: ({ dueDate }: { dueDate: string }) => changeTaskDueDate(dossierId, dueDate),
        onMutate: async ({ dueDate }) => {
            const dossierQueryKey = getDossierQueryKey(dossierId);

            await queryClient.cancelQueries({ queryKey: dossierQueryKey });

            const previousDossierData =
                queryClient.getQueryData<SerializedDossier>(dossierQueryKey);

            queryClient.setQueryData<SerializedDossier>(dossierQueryKey, (cur) => {
                if (!cur) return cur;
                return produce(cur, (draft) => {
                    if (draft.dossier[dossierId]?.task) {
                        draft.dossier[dossierId].task.dueDate = new Date(dueDate);
                    }
                });
            });

            return { previousDossierData };
        },
        onSuccess: () => refreshDossierAndPropagateToOverviews(dossierId),
        onError: (_, __, context) => {
            if (context?.previousDossierData) {
                queryClient.setQueryData(
                    getDossierQueryKey(dossierId),
                    context.previousDossierData,
                );
            }
            onErrorDefault(formatMessage);
        },
    });
};

export const useChangeTaskNoteMutation = (dossierId: DossierId) => {
    const { formatMessage } = useIntl();

    return useMutation({
        mutationKey: getChangeTaskNoteMutationKey(dossierId),
        mutationFn: ({ note }: { note: TaskNote }) => postChangeTaskNote(dossierId, note),
        onSuccess: () => refreshDossierAndPropagateToOverviews(dossierId),
        onError: () => onErrorDefault(formatMessage),
    });
};

export const useValidateDuplicateMutation = (dossierId: DossierId) => {
    const { formatMessage } = useIntl();

    return useMutation({
        mutationFn: () => validateDuplicate(dossierId),
        onSuccess: () => {
            deleteDossierInCache(dossierId);
            toast.success(
                formatMessage({
                    id: 'dossier.duplicate.validate_duplicate_dialog.notifications.success',
                }),
            );
        },
        onError: () => toast.error(formatMessage({ id: 'global.error.please_retry' })),
    });
};

export const useInvalidateDuplicateMutation = (dossierId: DossierId) => {
    const { formatMessage } = useIntl();

    return useMutation({
        mutationFn: () => invalidateDuplicate(dossierId),
        onSuccess: async () => {
            await refreshDossierAndPropagateToOverviews(dossierId);
            toast.success(formatMessage({ id: 'dossier.notifications.update_success' }));
        },
        onError: () => toast.error(formatMessage({ id: 'global.error.please_retry' })),
    });
};

export const useSpecifyDeferAgreementMutation = (dossierId: DossierId) => {
    const { formatMessage } = useIntl();

    return useMutation({
        mutationFn: ({
            amount,
            currency,
            expireAt,
        }: {
            amount: string;
            currency: string;
            expireAt: string | null;
        }) => specifyDeferAgreement(dossierId, amount, currency, expireAt),
        onSuccess: async () => {
            await refreshDossierAndPropagateToOverviews(dossierId);
            toast.success(formatMessage({ id: 'dossier.notifications.defer_agreement.success' }));
        },
        onError: () =>
            toast.error(formatMessage({ id: 'dossier.notifications.defer_agreement.error' })),
    });
};

export const useUploadVouchersMutation = (dossierId: DossierId) => {
    const { formatMessage } = useIntl();

    return useMutation({
        mutationFn: ({ vouchers, deferDate }: { vouchers: File[]; deferDate: string }) => {
            const formData = generateUploadVouchersFormData(vouchers, deferDate);
            return uploadVouchers(dossierId, formData);
        },
        onSuccess: async () => {
            await refreshDossierAndPropagateToOverviews(dossierId);
            toast.success(formatMessage({ id: 'dossier.notifications.upload_vouchers.success' }));
        },
        onError: () =>
            toast.error(formatMessage({ id: 'dossier.notifications.upload_vouchers.error' })),
    });
};

export const useAddVoucherMutation = (dossierId: DossierId) => {
    const { formatMessage } = useIntl();

    return useMutation({
        mutationFn: ({ file }: { file: File }) => {
            const formData = new FormData();
            formData.append('voucher', file);
            return addVoucher(dossierId, formData);
        },
        onSuccess: async () => {
            await refreshDossierAndPropagateToOverviews(dossierId);
            toast.success(formatMessage({ id: 'dossier.notifications.add_voucher.success' }));
        },
        onError: () =>
            toast.error(formatMessage({ id: 'dossier.notifications.add_voucher.error' })),
    });
};

export const useDeleteVoucherMutation = (dossierId: DossierId) => {
    const { formatMessage } = useIntl();

    return useMutation({
        mutationFn: ({ voucherId }: { voucherId: string }) => deleteVoucher(dossierId, voucherId),
        onSuccess: async () => {
            await refreshDossierAndPropagateToOverviews(dossierId);
            toast.success(formatMessage({ id: 'dossier.notifications.delete_voucher.success' }));
        },
        onError: () =>
            toast.error(formatMessage({ id: 'dossier.notifications.delete_voucher.error' })),
    });
};

export const useReopenMutation = (dossierId: DossierId) => {
    const { formatMessage } = useIntl();

    return useMutation({
        mutationFn: () => reopen(dossierId),
        onSuccess: async () => {
            await refreshDossierAndPropagateToOverviews(dossierId);
            toast.success(formatMessage({ id: 'dossier.notifications.success' }));
        },
        onError: () => onErrorDefault(formatMessage),
    });
};

export const useChangeMemoMutation = (dossierId: DossierId) => {
    const { formatMessage } = useIntl();

    return useMutation({
        mutationFn: ({ memo }: { memo: Memo }) => changeMemo(dossierId, memo),
        onMutate: async ({ memo }) => {
            const dossierQueryKey = getDossierQueryKey(dossierId);
            await queryClient.cancelQueries({ queryKey: dossierQueryKey });

            const previousDossierData =
                queryClient.getQueryData<SerializedDossier>(dossierQueryKey);

            queryClient.setQueryData<SerializedDossier>(dossierQueryKey, (cur) => {
                if (!cur) return cur;
                return produce(cur, (draft) => {
                    if (!draft.dossierMemo) {
                        draft.dossierMemo = {};
                    }
                    draft.dossierMemo[dossierId] = memo;
                });
            });

            return { previousDossierData };
        },
        onSuccess: async () => {
            await refreshDossierAndPropagateToOverviews(dossierId);
        },
        onError: (_, __, context) => {
            if (context?.previousDossierData) {
                queryClient.setQueryData(
                    getDossierQueryKey(dossierId),
                    context.previousDossierData,
                );
            }
            onErrorDefault(formatMessage);
        },
    });
};

const debounceShowSuccess = debounce(1000, (formatMessage: IntlShape['formatMessage']) => {
    toast.success(formatMessage({ id: 'dossier.notifications.update_success' }));
});

export const useUpdateHeatLevelMutation = (dossierId: DossierId) => {
    const { formatMessage } = useIntl();

    return useMutation({
        mutationFn: ({ heatLevel }: { heatLevel: number }) => updateHeatLevel(dossierId, heatLevel),
        onMutate: async ({ heatLevel }) => {
            queryClient.setQueryData<SerializedDossier>(getDossierQueryKey(dossierId), (cur) => {
                if (!cur) return cur;
                return produce(cur, (draft) => {
                    draft.dossier[dossierId].heatLevel = heatLevel;
                });
            });
        },
        onSuccess: async () => {
            await refreshDossierAndPropagateToOverviews(dossierId);
            debounceShowSuccess(formatMessage);
        },
        onError: () => onErrorDefault(formatMessage),
    });
};

const onErrorDefault = (formatMessage: IntlShape['formatMessage']) => {
    toast.error(formatMessage({ id: 'dossier.notifications.error' }));
};
